import { useState } from 'react';
import { Building2, Plus, Tags } from 'lucide-react';
import { Button } from '@/components/Button';
import { Field } from '@/components/Field';
import { Modal } from '@/components/Modal';
import { Pill } from '@/components/StatusPill';
import { EmptyState, ErrorState, Skeleton } from '@/components/States';
import { useBrand, useBrands, useSaveTopic } from '@/hooks/use-brands';
import { describeError } from '@/lib/describe-error';
import { Roles, type Session } from '@/auth/keycloak';
import type { BrandDetail, TopicSummary } from '@/lib/schemas';
import {
  BrandSelector,
  Checkbox,
  ReadOnlyNotice,
} from '@/pages/settings/settings-ui';
import { inputClass, textareaClass, useSelectedBrand } from '@/pages/settings/settings-shared';

/**
 * Topics — what recipients can opt into or out of (spec § 4.9).
 *
 * ── WHY THIS SCREEN HAD TO EXIST ──────────────────────────────────────────────────
 *
 * Nothing could create a topic. The table existed, the preference centre read it, campaigns could
 * declare one and the audience resolution honoured it — and there was no way to put a row in it. So
 * the preference centre offered recipients an empty list and the whole mechanism was unreachable. The
 * enforcement was built on top of that gap without anybody noticing.
 *
 * ── A TOPIC IS RETIRED, NEVER DELETED ─────────────────────────────────────────────
 *
 * There is no delete control and no endpoint behind one. Recipients hold preferences against a topic
 * and sent campaigns declared it, so removing the row would erase both what somebody chose and what a
 * campaign was about. Retiring takes it out of the preference centre and the campaign picker and
 * leaves the history answerable — the same treatment a retired sender address gets.
 *
 * ── THE DESCRIPTION IS NOT OPTIONAL ───────────────────────────────────────────────
 *
 * Recipients read it. "Marketing" with no explanation is not a choice somebody can make, and the
 * whole point of a preference centre is informed consent, so the server requires it and so does this
 * form.
 */
export function TopicsPage({ session }: { session: Session }) {
  const isAdministrator = session.roles.includes(Roles.Admin);

  const { data, isPending, isError, error, refetch } = useBrands();
  const [brandId, setBrandId] = useSelectedBrand((data?.items ?? []).map((brand) => brand.id));

  return (
    <div className="px-4 py-6 sm:px-6">
      <div>
        <h1 className="text-xl font-semibold text-heading">Topics</h1>
        <p className="mt-1 text-sm text-muted">
          What recipients can choose to receive. A campaign says which topic it is about, and anyone who
          turned that topic off is left out automatically.
        </p>
      </div>

      {!isAdministrator && <ReadOnlyNotice />}

      {isPending ? (
        <div className="mt-5 flex flex-col gap-3">
          <Skeleton className="h-10" />
          <Skeleton className="h-48" />
        </div>
      ) : isError ? (
        <div className="mt-5">
          <ErrorState error={error} onRetry={() => void refetch()} />
        </div>
      ) : data.items.length === 0 ? (
        <div className="mt-5 rounded-lg border border-border bg-surface">
          <EmptyState
            icon={Building2}
            title="No brands yet"
            description="A topic belongs to a brand, so a brand has to exist first."
          />
        </div>
      ) : (
        <>
          <BrandSelector brands={data.items} selectedId={brandId} onSelect={setBrandId} />

          {brandId !== undefined && (
            <BrandTopics brandId={brandId} isAdministrator={isAdministrator} />
          )}
        </>
      )}
    </div>
  );
}

function BrandTopics({ brandId, isAdministrator }: { brandId: string; isAdministrator: boolean }) {
  const { data, isPending, isError, error, refetch } = useBrand(brandId);
  const [editing, setEditing] = useState<{ existing?: TopicSummary } | null>(null);

  if (isPending) return <Skeleton className="mt-5 h-48" />;

  if (isError) {
    return (
      <div className="mt-5">
        <ErrorState error={error} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="mt-5">
      <section className="overflow-hidden rounded-lg border border-border bg-surface">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
          <h2 className="text-sm font-semibold text-heading">Topics</h2>

          {/* Not while the list is empty — the empty state already carries this action, and two
              identical buttons an inch apart is a choice the reader has to think about for nothing. */}
          {isAdministrator && data.topics.length > 0 && (
            <Button variant="secondary" size="sm" onClick={() => setEditing({})}>
              <Plus className="size-3.5" aria-hidden="true" />
              Add a topic
            </Button>
          )}
        </div>

        {data.topics.length === 0 ? (
          <EmptyState
            icon={Tags}
            title="No topics yet"
            description="Without a topic, the preference centre offers recipients nothing to choose between and a campaign cannot say what it is about. One or two broad topics is usually better than ten narrow ones."
            action={
              isAdministrator ? (
                <Button variant="primary" onClick={() => setEditing({})}>
                  <Plus className="size-4" aria-hidden="true" />
                  Add a topic
                </Button>
              ) : undefined
            }
          />
        ) : (
          <ul className="divide-y divide-border">
            {data.topics.map((topic) => (
              <li key={topic.id} className="flex flex-wrap items-start gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-heading">
                    {topic.name}
                    {!topic.isActive && <Pill tone="neutral">Retired</Pill>}
                  </p>

                  {/* Shown because this is the text recipients read, and seeing it here is the only
                      way to notice it has gone stale. */}
                  <p className="mt-0.5 text-xs text-muted">{topic.description}</p>
                </div>

                {isAdministrator && (
                  <Button
                    variant="ghost"
                    size="sm"
                    // Named, so a screen-reader user can tell one Edit from the next.
                    aria-label={`Edit ${topic.name}`}
                    onClick={() => setEditing({ existing: topic })}
                  >
                    Edit
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {editing !== null && (
        <TopicForm
          brand={data}
          existing={editing.existing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function TopicForm({
  brand,
  existing,
  onClose,
}: {
  brand: BrandDetail;
  /** Explicitly `| undefined` rather than optional: exactOptionalPropertyTypes distinguishes an absent
      prop from one passed as undefined, and the caller passes it. */
  existing: TopicSummary | undefined;
  onClose: () => void;
}) {
  const save = useSaveTopic(brand.id);

  const [name, setName] = useState(existing?.name ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [isActive, setIsActive] = useState(existing?.isActive ?? true);
  const [touched, setTouched] = useState<{ name?: boolean; description?: boolean }>({});

  const errors = {
    name: name.trim() === '' ? 'Give the topic a name.' : undefined,
    description:
      description.trim() === ''
        ? 'Recipients see this. A topic they cannot understand is not a choice.'
        : undefined,
  };

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();

    if (errors.name !== undefined || errors.description !== undefined) {
      setTouched({ name: true, description: true });
      document.getElementById(errors.name !== undefined ? 'name' : 'description')?.focus();
      return;
    }

    save.mutate(
      {
        topicId: existing?.id ?? null,
        // DisplayOrder is not exposed: with a handful of topics the order they were created in is
        // fine, and a spinner nobody understands is worse than an arbitrary order.
        values: { name: name.trim(), description: description.trim(), displayOrder: 0, isActive },
      },
      { onSuccess: onClose },
    );
  }

  return (
    <Modal
      title={existing === undefined ? 'Add a topic' : `Edit ${existing.name}`}
      description={`Recipients of ${brand.name} mail choose these in the preference centre.`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>

          <Button type="submit" form="topic-form" variant="primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <form id="topic-form" onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        {save.isError && (
          <p role="alert" className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-ink">
            {describeError(save.error).message}
          </p>
        )}

        <Field
          id="name"
          label="Name"
          hint="What recipients see. Short and plain — “Product updates”, not “Q3 lifecycle comms”."
          error={touched.name === true ? errors.name : undefined}
        >
          <input
            id="name"
            type="text"
            value={name}
            maxLength={120}
            onChange={(event) => setName(event.target.value)}
            onBlur={() => setTouched((previous) => ({ ...previous, name: true }))}
            aria-invalid={touched.name === true && errors.name !== undefined}
            aria-describedby={errors.name === undefined ? 'name-hint' : 'name-error'}
            className={inputClass}
          />
        </Field>

        <Field
          id="description"
          label="Description"
          hint="Also shown to recipients. Say what they will actually get, and roughly how often."
          error={touched.description === true ? errors.description : undefined}
        >
          <textarea
            id="description"
            rows={3}
            value={description}
            maxLength={500}
            onChange={(event) => setDescription(event.target.value)}
            onBlur={() => setTouched((previous) => ({ ...previous, description: true }))}
            aria-invalid={touched.description === true && errors.description !== undefined}
            aria-describedby={errors.description === undefined ? 'description-hint' : 'description-error'}
            className={textareaClass}
          />
        </Field>

        <Checkbox
          id="isActive"
          label="Offer this topic"
          hint="Turn off to retire it. Retiring hides it from the preference centre and from the campaign picker, and keeps every choice recipients have already made — there is no delete, on purpose."
          checked={isActive}
          onChange={setIsActive}
        />
      </form>
    </Modal>
  );
}
